/** Days after which care items are flagged for admin reports */
export const FARRIER_DUE_DAYS = 56; // ~8 weeks
export const RABIES_DUE_DAYS = 120;
export const WORMER_DUE_DAYS = 90;
export const COGGINS_DUE_DAYS = 365;

export type AttentionLevel = 'ok' | 'due_soon' | 'overdue' | 'missing';

export function parseDateOnly(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value.includes('T') ? value : `${value}T12:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

export function daysSince(value?: string | null): number | null {
  const d = parseDateOnly(value);
  if (!d) return null;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}

export function attentionForAge(
  value: string | null | undefined,
  maxDays: number,
  dueSoonWindow = 14
): AttentionLevel {
  if (!value) return 'missing';
  const days = daysSince(value);
  if (days === null) return 'missing';
  if (days > maxDays) return 'overdue';
  if (days > maxDays - dueSoonWindow) return 'due_soon';
  return 'ok';
}

export function formatReportDate(value?: string | null): string {
  const d = parseDateOnly(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function cogginsDocumentStatus(horse: {
  lastCoggins?: string | null;
  cogginsDocumentUrl?: string | null;
}): AttentionLevel {
  if (!horse.cogginsDocumentUrl) return 'missing';
  const dateLevel = attentionForAge(horse.lastCoggins, COGGINS_DUE_DAYS);
  if (dateLevel === 'overdue' || dateLevel === 'missing') return dateLevel;
  return 'ok';
}

export function farrierAttention(lastSeenByFarrier?: string | null): AttentionLevel {
  return attentionForAge(lastSeenByFarrier, FARRIER_DUE_DAYS);
}

export function medicalAttentionScore(horse: {
  lastRabies?: string | null;
  lastWormer?: string | null;
  lastCoggins?: string | null;
  cogginsDocumentUrl?: string | null;
}): number {
  const levels = [
    attentionForAge(horse.lastRabies, RABIES_DUE_DAYS),
    attentionForAge(horse.lastWormer, WORMER_DUE_DAYS),
    attentionForAge(horse.lastCoggins, COGGINS_DUE_DAYS),
    cogginsDocumentStatus(horse)
  ];
  const weights: Record<AttentionLevel, number> = {
    missing: 3,
    overdue: 2,
    due_soon: 1,
    ok: 0
  };
  return levels.reduce((sum, l) => sum + weights[l], 0);
}

export const attentionLabel: Record<AttentionLevel, string> = {
  ok: 'OK',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  missing: 'Missing'
};

export const attentionClass: Record<AttentionLevel, string> = {
  ok: 'bg-green-100 text-green-800',
  due_soon: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  missing: 'bg-gray-200 text-gray-800'
};
